import { Role } from 'auth';
import { isFunction } from 'lodash';
import { DateTime } from 'luxon';
import { isSafari } from 'react-csv/lib/core';
import { CLIENT_ID, COGSWORTH_URL, TEAMSNAP_URL } from './constants';

type ReportExport = {
  fileName: string;
  fileExtension: string;
  fileData: string;
};

export function isLocal() {
  return (
    window.location.href.indexOf('http://localhost:') === 0 ||
    window.location.href.indexOf('https://ts-checkr.ngrok.io') === 0
  );
}

export function getCurrentOrgId() {
  try {
    return parseInt(window.location.href.match(/organizations\/(\d+)/)[1]);
  } catch (error) {
    return '';
  }
}

export function currentBaseUrl() {
  const { protocol, host } = window.location;

  return `${protocol}//${host}`;
}

export const ORG_DEPOSIT_ACCOUNTS_URL = (orgId: number) =>
  `${currentBaseUrl()}/organizations/${orgId}/settings/merchant-accounts`;

export const ORG_DEPOSIT_ACCOUNTS_FINANCIALS_URL = (
  orgId: number,
  programId: number,
  seasonId: number
) =>
  `${currentBaseUrl()}/organizations/${orgId}/programs/${programId}/seasons/${seasonId}/financials`;

export function redirectToOrg(orgId) {
  window.location.href = `${currentBaseUrl()}/organizations/${orgId}`;
}

export function redirectToTeamsnapTeamDashboard() {
  window.location.href = `${TEAMSNAP_URL}/team/dashboard`;
}

export function redirectToLogin(redirectUrl?: string) {
  redirectUrl = redirectUrl || window.location.href;
  window.location.href = `${COGSWORTH_URL}login?client_id=${CLIENT_ID}&redirect_uri=${redirectUrl}`;
}

export function redirectToSignup(redirectUrl?: string) {
  redirectUrl = redirectUrl || window.location.href;
  window.location.href = `${COGSWORTH_URL}user_accounts?client_id=${CLIENT_ID}&redirect_uri=${redirectUrl}`;
}

export function isEmpty(value: string): boolean {
  return !value || value.trim() === '';
}

export function capitalize(value: string) {
  if (isEmpty(value)) return value;

  return value.substring(0, 1).toUpperCase() + value.substring(1);
}

export function capitalize2(value: string) {
  if (isEmpty(value)) return value;

  return value.split(' ').map(capitalize).join(' ');
}

export function isNotEmpty(value: string) {
  return !isEmpty(value);
}

export function now(): DateTime {
  return DateTime.local();
}

export function parseIsoDate(date: string): DateTime {
  if (!date || date.length < 10) return null;

  if (date.length > 10) {
    date = date.substring(0, 10);
  }

  return DateTime.fromISO(date);
}

export function parseDate(date: string, format: string): DateTime {
  if (!date) return null;

  return DateTime.fromFormat(date, format);
}

export function parseUtcToIsoDateTime(date: string): DateTime {
  const dateIsoString = new Date(date).toISOString();
  return DateTime.fromISO(dateIsoString);
}

export function parseIsoDateTime(date: string): DateTime {
  return DateTime.fromISO(date);
}

export function toIsoDate(dateTime: DateTime): string {
  if (!dateTime) return null;

  return dateTime.toString().substring(0, 10);
}

export function toIsoDateTime(dateTime: DateTime): string {
  if (!dateTime) return null;

  return dateTime.toString();
}

export const toDateMED = (date: string) =>
  DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED);

export function isField(object: any, key: string, prototype?: any): boolean {
  if (!prototype) {
    prototype = Object.getPrototypeOf(object);
  }

  if (isFunction(object[key])) return false;

  const desc = Object.getOwnPropertyDescriptor(prototype, key);
  const hasGetter = desc && typeof desc.get === 'function';
  return !hasGetter;
}

export function getParameterByName(url: string, name: string) {
  if (!url) url = window.location.href;
  name = name.replace(/[[\]]/g, '\\$&');
  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

export function convertToPennies(value: string): string | number {
  const pennies = value.trim().replace('$', '');

  return pennies ? Math.round(+Number(pennies).toFixed(2) * 100) : pennies;
}

export function convertFromPennies(value: number): string | number {
  if (value === 0) {
    return Number(value).toFixed(2);
  }
  return value ? Number(value / 100).toFixed(2) : value;
}

export function currencyGivenPennies(value: number): string {
  if (value === null || value === undefined) return '';

  const dollars = convertFromPennies(Math.abs(value));

  return value < 0 ? `($${dollars})` : `$${dollars}`;
}

export function isOwner(staff: any) {
  return (
    staff.roles.map((role: { type: any }) => role.type).includes(Role.Owner) &&
    staff.invitationStatus.toLowerCase() === 'accepted'
  );
}

export const sortByAlpha = (a, b) => {
  const aString = a.toLowerCase();
  const bString = b.toLowerCase();

  if (aString === bString) {
    return 0;
  }
  return aString < bString ? -1 : 1;
};

export const sortByProperty = (propertyName: string) => (a, b) =>
  sortByAlpha(a[propertyName], b[propertyName]);

export const sortByName = sortByProperty('name');

export const groupBy = (key, array) => {
  const group = array.reduce((r, a) => {
    r[a[key]] = [...(r[a[key]] || []), a];
    return r;
  }, {});
  return group;
};

export const displayLastNameFirst = ({ firstName, lastName }) => {
  let name = '';
  if (firstName && firstName !== null) {
    name = `${firstName}`;
  }
  if (lastName && lastName !== null) {
    name = `${lastName}, ${name}`;
  }
  return name;
};

export const displayFirstNameLast = ({ firstName, lastName }) => {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  return firstName ? firstName : lastName;
};

export const addToArrayWhen = <T>(items: [T, boolean][], arr: T[]) =>
  items.reduce(
    (prev, [value, condition]) => (condition ? [...prev, value] : prev),
    arr
  );

export const countBy = <T>(array: T[], map: (x: T) => number) =>
  array.reduce((previous, current) => previous + (map(current) || 0), 0);

export const createFilterID = (value: string) =>
  value
    .replace(/[^\w\s]/gi, '')
    .replace(/ /gi, '-')
    .toLowerCase();

export const getAbbreviatedTimezone = () =>
  new Date()
    .toLocaleDateString(undefined, { timeZoneName: 'short' })
    .split(', ')[1];

export function isValidDate(d: unknown) {
  return d instanceof Date && !isNaN(d.getTime());
}

export const generateCSVFileDownload = (reportExport: ReportExport) => {
  const csv = reportExport.fileData;
  const type = isSafari() ? 'application/csv' : 'text/csv';
  const blob = new Blob(['', csv], { type: type });
  const fileName = reportExport.fileName + '.' + reportExport.fileExtension;
  const dataURI = 'data:' + type + 'charset=utf-8' + csv;
  const URL = window.URL || window.webkitURL;
  const file =
    typeof URL.createObjectURL === 'undefined'
      ? dataURI
      : URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.download = fileName;
  anchor.href = file;
  anchor.click();
};

// Unlike `currencyGivenPennies`, this function will include the ',' in numbers like $2,000.00
export function formatPenniesToDollars(pennies: number): string {
  const dollars = pennies / 100;
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

export const genericErrorMessage =
  'Oops! Something went wrong. Please try again or contact TeamSnap support for help.';

export function parseErrorMessage(e: any, fallbackMessage?: string): string {
  if (e?.response?.status === 500) {
    return genericErrorMessage;
  }
  if (e?.response?.data) {
    return e.response.data.error || e.message;
  }
  return fallbackMessage || genericErrorMessage;
}
